import { useCallback, useEffect, useMemo, useRef, type CSSProperties } from "react";
import { observer } from "mobx-react-lite";
import { Image, Input, Modal, Spin, message } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { EasyAvatar } from "fin-web-components";
import { globalModel } from "../../global/GlobalModel";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { useSafeAreaTopPad } from "../../utils/useSafeAreaTopPad";
import { SpaceCustomerServiceModel } from "./SpaceCustomerServiceModel";
import type { CsMessage } from "./SpaceCustomerServiceTypes";
import { CS_ASSISTANT_AVATAR_URL, CS_HISTORY_RECYCLED_HINT } from "./SpaceCustomerServiceTypes";
import { CsBroadcastMessageCard } from "./CsBroadcastMessageCard";
import { CsRenewalProductCard } from "./CsRenewalProductCardView";
import { CsMessageTextContent } from "./CsMessageTextContent";
import { useCsKeyboardLayout, CS_KEYBOARD_OPEN_CLASS } from "./useCsKeyboardLayout";
import { getReadableTime, parseReadableDate } from "../../utils/getReadableTime";
import "../../components/common/H5PromptModal.css";
import "./SpaceCustomerServiceStyle.css";

const CS_AVATAR = `${process.env.PUBLIC_URL || ""}/icons/tab-cs-active.svg`;

function shouldShowTimeDivider(prev?: CsMessage, curr?: CsMessage): boolean {
  if (curr?.messageType === "SYSTEM_HINT") return false;
  if (!curr?.sentAt) return false;
  if (!prev?.sentAt) return true;
  const a = parseReadableDate(prev.sentAt).getTime();
  const b = parseReadableDate(curr.sentAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return b - a > 5 * 60 * 1000;
}

function resolveMessageImageSrc(m: CsMessage): string {
  if (m.localPreviewUrl?.trim()) return m.localPreviewUrl;
  return resolveAppMediaUrl(m.content) ?? m.content ?? "";
}

const CS_IMAGE_UPLOAD_DEBUG = process.env.NODE_ENV === "development";

function showImageSendRetryModal(onRetry: () => void) {
  const modal = Modal.info({
    className: "lf-h5-prompt-modal",
    icon: null,
    centered: true,
    width: 320,
    closable: true,
    maskClosable: true,
    footer: null,
    content: (
      <>
        <div className="lf-h5-prompt-modal__body">
          <p className="lf-h5-prompt-modal__title">发送失败</p>
          <p className="lf-h5-prompt-modal__desc">是否重新发送？</p>
        </div>
        <div className="lf-h5-prompt-modal__actions">
          <button
            type="button"
            className="lf-h5-prompt-modal__btn lf-h5-prompt-modal__btn--ghost"
            onClick={() => modal.destroy()}
          >
            否
          </button>
          <button
            type="button"
            className="lf-h5-prompt-modal__btn lf-h5-prompt-modal__btn--primary"
            onClick={() => {
              modal.destroy();
              onRetry();
            }}
          >
            是
          </button>
        </div>
      </>
    ),
  });
}

function showImageSendFailureModal(
  log: string,
  opts: { onClose: () => void; onRetry?: () => void }
) {
  const copyLog = () => {
    void copyTextToClipboard(log).then((ok) => {
      if (ok) message.success("日志已复制");
      else message.warning("复制失败，请手动选择日志");
    });
  };

  const modal = Modal.info({
    className: "lf-h5-prompt-modal space-cs-image-fail-modal",
    icon: null,
    centered: true,
    width: "min(360px, calc(100vw - 32px))",
    closable: true,
    maskClosable: true,
    footer: null,
    content: (
      <>
        <div className="lf-h5-prompt-modal__body lf-h5-prompt-modal__body--left space-cs-image-fail-modal__body">
          <p className="lf-h5-prompt-modal__title">图片发送失败</p>
          <div className="space-cs-image-fail-modal__log-head">
            <span className="space-cs-image-fail-modal__log-label">过程日志</span>
            <button type="button" className="space-cs-image-fail-modal__copy" onClick={copyLog}>
              复制日志
            </button>
          </div>
          <pre className="space-cs-image-fail-modal__log">{log || "暂无日志"}</pre>
        </div>
        <div className="lf-h5-prompt-modal__actions space-cs-image-fail-modal__actions">
          <button
            type="button"
            className="lf-h5-prompt-modal__btn lf-h5-prompt-modal__btn--ghost"
            onClick={() => {
              modal.destroy();
              opts.onClose();
            }}
          >
            关闭
          </button>
          {opts.onRetry ? (
            <button
              type="button"
              className="lf-h5-prompt-modal__btn lf-h5-prompt-modal__btn--primary"
              onClick={() => {
                modal.destroy();
                opts.onClose();
                opts.onRetry?.();
              }}
            >
              重新发送
            </button>
          ) : null}
        </div>
      </>
    ),
  });
}

export const SpaceCustomerServicePage = observer(function SpaceCustomerServicePage(props: {
  spaceId: string;
  spaceName?: string;
  entryDisplayName?: string;
  onUnreadChange?: (count: number) => void;
  /** 嵌入消息中心聊天壳：隐藏页内顶栏 */
  embedded?: boolean;
}) {
  const spaceName = props.spaceName?.trim() ?? "";
  const chatTitle = props.entryDisplayName?.trim() || (spaceName ? `${spaceName}服务专员` : "服务专员");
  const displayName = spaceName ? `${spaceName}客服` : "空间客服";
  const model = useMemo(
    () =>
      new SpaceCustomerServiceModel(props.spaceId, displayName, {
        onUnreadChange: props.onUnreadChange,
      }),
    [props.spaceId, displayName, props.onUnreadChange]
  );
  const shellRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const stickBottomRef = useRef(true);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const lastFailureAlertKeyRef = useRef<string | null>(null);
  const safeTopPad = useSafeAreaTopPad(headerRef, {
    pauseWhile: () => document.documentElement.classList.contains(CS_KEYBOARD_OPEN_CLASS),
  });

  const scrollToLatest = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, []);

  const { onInputFocus: openKeyboardLayout, onInputBlur, onComposePointerDown, closeKeyboardLayout } =
    useCsKeyboardLayout(shellRef);

  const onInputFocus = useCallback(() => {
    openKeyboardLayout();
    if (stickBottomRef.current) {
      requestAnimationFrame(scrollToLatest);
    }
  }, [openKeyboardLayout, scrollToLatest]);

  useEffect(() => {
    void model.bootstrap();
    return () => model.dispose();
  }, [model]);

  useEffect(() => {
    if (!stickBottomRef.current) return;
    scrollToLatest();
  }, [model.messages.length, scrollToLatest]);

  useEffect(() => {
    if (!stickBottomRef.current || model.messages.length === 0) {
      return;
    }
    scrollToLatest();
  }, [model.loading, model.messages.length, scrollToLatest]);

  useEffect(() => {
    if (!CS_IMAGE_UPLOAD_DEBUG) return;
    const alert = model.imageSendFailureAlert;
    if (!alert) {
      lastFailureAlertKeyRef.current = null;
      return;
    }
    const alertKey = `${alert.messageId}:${alert.log}`;
    if (lastFailureAlertKeyRef.current === alertKey) return;
    lastFailureAlertKeyRef.current = alertKey;
    showImageSendFailureModal(alert.log, {
      onClose: () => model.clearImageSendFailureAlert(),
      onRetry: () => model.retryFailedImage(alert.messageId),
    });
  }, [model, model.imageSendFailureAlert]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop <= 24 && model.hasOlder && !model.loadingOlder && !model.historyRecycled) {
      void model.loadOlder();
    }
    stickBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const userName = globalModel.currentUser?.name ?? "我";
  const userAvatar = resolveAppMediaUrl(globalModel.currentUser?.avatarUrl);

  const pageStyle: CSSProperties | undefined =
    !props.embedded && safeTopPad > 0
      ? ({ "--space-cs-header-safe-top-pad": `${safeTopPad}px` } as CSSProperties)
      : undefined;

  return (
    <div className={`space-cs-page${props.embedded ? " space-cs-page--embedded" : ""}`} style={pageStyle}>
      <div ref={shellRef} className="space-cs-page-shell">
      {!props.embedded ? (
      <header ref={headerRef} className="space-cs-header">
        <h1 className="space-cs-header__title">{chatTitle}</h1>
      </header>
      ) : null}
      <div className="space-cs-chat">
        <div ref={scrollRef} className="space-cs-scroll" onScroll={onScroll}>
          <div className="space-cs-scroll-inner">
        <div className="space-cs-scroll-spacer" aria-hidden />
        {model.loadingOlder ? <div className="space-cs-loading-top">加载更早消息…</div> : null}
        {model.historyRecycled ? <div className="space-cs-history-tip">{CS_HISTORY_RECYCLED_HINT}</div> : null}
        {model.loading && model.messages.length === 0 ? (
          <div className="space-cs-loading">
            <Spin />
          </div>
        ) : null}
        {model.messages.map((m, idx) => {
          const prev = idx > 0 ? model.messages[idx - 1] : undefined;
          if (m.messageType === "SYSTEM_HINT") {
            return (
              <div key={m.id} className="space-cs-time">
                {m.content}
              </div>
            );
          }
          const isSelf = m.senderType === "CUSTOMER";
          const staffName = m.senderName?.trim() || displayName;
          const isAssistant = m.messageType === "RENEWAL_CARD";
          const staffAvatar = isAssistant
            ? CS_ASSISTANT_AVATAR_URL
            : resolveAppMediaUrl(m.senderAvatarUrl) || undefined;
          const showImageFail = isSelf && m.messageType === "IMAGE" && m.imageSendStatus === "failed";
          const imageSrc =
            m.messageType === "IMAGE" && (m.localPreviewUrl?.trim() || m.content?.trim())
              ? resolveMessageImageSrc(m)
              : "";
          return (
            <div key={m.id}>
              {shouldShowTimeDivider(prev, m) ? (
                <div className="space-cs-time">{getReadableTime(m.sentAt)}</div>
              ) : null}
              <div className={`space-cs-msg-row${isSelf ? " space-cs-msg-row--self" : ""}`}>
                <EasyAvatar
                  shape="square"
                  squareRadius={4}
                  size={36}
                  url={isSelf ? userAvatar || undefined : staffAvatar || CS_AVATAR}
                  word={avatarDisplayWord(isSelf ? userName : staffName)}
                  alt=""
                />
                <div className="space-cs-msg-col">
                  {!isSelf ? <div className="space-cs-msg-name">{staffName}</div> : null}
                  <div
                    className={`space-cs-msg-body-line${isSelf ? " space-cs-msg-body-line--self" : ""}`}
                  >
                    {showImageFail ? (
                      <button
                        type="button"
                        className="space-cs-outbound-fail-hit"
                        aria-label="发送失败"
                        onClick={() => {
                          if (CS_IMAGE_UPLOAD_DEBUG) {
                            showImageSendFailureModal(
                              model.getImageSendDebugLog(m.id) ?? "暂无日志",
                              {
                                onClose: () => {},
                                onRetry: () => model.retryFailedImage(m.id),
                              }
                            );
                          } else {
                            showImageSendRetryModal(() => model.retryFailedImage(m.id));
                          }
                        }}
                      >
                        <ExclamationCircleFilled className="space-cs-outbound-fail-icon" />
                      </button>
                    ) : null}
                    <div
                      className={`space-cs-msg-bubble${isSelf ? " space-cs-msg-bubble--self" : ""}${
                        m.messageType === "BROADCAST" ? " space-cs-msg-bubble--broadcast" : ""
                      }${m.messageType === "RENEWAL_CARD" ? " space-cs-msg-bubble--renewal" : ""}${
                        m.messageType === "IMAGE" ? " space-cs-msg-bubble--image" : ""
                      }`}
                    >
                      {m.recalled ? (
                        <span className="space-cs-msg-recalled">已撤回</span>
                      ) : m.messageType === "BROADCAST" ? (
                        <CsBroadcastMessageCard content={m.content} />
                      ) : m.messageType === "RENEWAL_CARD" ? (
                        <CsRenewalProductCard content={m.content} />
                      ) : m.messageType === "IMAGE" && imageSrc ? (
                        <Image
                          src={imageSrc}
                          alt="图片消息"
                          className="space-cs-msg-image"
                          preview={{
                            mask: false,
                            destroyOnClose: true,
                            zIndex: 2000,
                            rootClassName: "space-cs-broadcast-image-preview",
                          }}
                        />
                      ) : (
                        <CsMessageTextContent content={m.content ?? ""} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
          </div>
        </div>
        <div className="space-cs-compose">
        <div className="space-cs-compose-input-wrap">
        <Input.TextArea
          ref={inputRef}
          value={model.draft}
          onChange={(e) => model.setDraft(e.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void model.send();
            }
          }}
          placeholder="输入消息"
          disabled={model.sending || !model.conversationId}
          autoSize={{ minRows: 1, maxRows: 4 }}
          className="space-cs-compose-input"
        />
        </div>
        <button
          type="button"
          className="space-cs-compose-img-btn"
          disabled={!model.conversationId}
          onClick={() => imageInputRef.current?.click()}
          aria-label="发送图片"
        >
          <span className="space-cs-compose-plus-icon" aria-hidden />
        </button>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
          className="space-cs-compose-file-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            if (!file.type.startsWith("image/")) {
              message.warning("请选择图片文件");
              return;
            }
            void model.sendImage(file);
          }}
        />
        <button
          type="button"
          className={`space-cs-send-btn${model.canSend ? " space-cs-send-btn--active" : ""}`}
          disabled={!model.canSend}
          onPointerDown={(e) => {
            e.preventDefault();
            onComposePointerDown();
          }}
          onClick={() => {
            void model.send();
            inputRef.current?.blur();
            closeKeyboardLayout();
          }}
        >
          发送
        </button>
        </div>
      </div>
      </div>
    </div>
  );
});

export default SpaceCustomerServicePage;
